/**
 * PanoramaEffect creates a cylindrical panorama feel — like standing inside a
 * spinning carousel looking outward. The center slide faces the viewer flat
 * (no rotation). Slides toward the edges progressively rotate via rotateY with
 * 3D perspective, creating a convex/panoramic arc where outer edges curve away.
 *
 * Options (namespaced under `panorama`):
 * - panorama.angle: Max rotation angle in degrees for outermost slides (default: 26)
 * - panorama.perspective: CSS perspective value in px (default: 1200)
 * - panorama.angularStep: Degrees of rotation added per slide from center (default: 15)
 * - panorama.scaleStep: Scale increase per slide-slot from center (default: 0.1)
 */

// Factory function - class is created when TarotEffect is available
function createPanoramaEffect(TarotEffect) {
	return class PanoramaEffect extends TarotEffect {
		static effectName = 'panorama';

		static defaultOptions = {
			panorama: { perspective: 1200, angularStep: 15, angle: 26, scaleStep: 0.1 },
		};

		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: Infinity,
			loopBuffer: { left: 2, right: 2 },
		};

		/**
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			this.#loadOptions();
			this.init();
		}

		#loadOptions() {
			const opts = this.ctx.store.getOptions().panorama || {};
			this.perspective = opts.perspective;
			this.angularStep = opts.angularStep;
			this.maxAngle = opts.angle;
			this.scaleStep = opts.scaleStep;
		}

		init() {
			super.init();
		}

		reInit() {
			super.reInit();
			this.#loadOptions();
		}

		/**
		 * Calculates the projected width of a slide after rotation under
		 * perspective, with transform-origin at the hinge edge.
		 *
		 * The rotation direction (rotateY(-angle) for right, rotateY(+angle)
		 * for left) brings the far edge TOWARDS the viewer (positive z in CSS).
		 * Under perspective, closer objects appear larger, so the denominator
		 * is (1 - depth/p), not (1 + depth/p).
		 *
		 * @param {number} w - Effective slide width in px (after scale)
		 * @param {number} thetaDeg - Rotation angle in degrees (always positive)
		 * @param {number} p - Perspective distance in px
		 * @returns {number} Projected width in px
		 */
		getProjectedWidth(w, thetaDeg, p) {
			const theta = (thetaDeg * Math.PI) / 180;
			const denom = 1 - (w * Math.sin(theta)) / p;
			// Guard against singularity (edge at or past the viewer)
			if (denom <= 0.01) return w * 100;
			return (w * Math.cos(theta)) / denom;
		}

		/**
		 * Main render function called every animation frame.
		 *
		 * Four passes:
		 *   1. Calculate rotation angle + trim for each slide
		 *   2. L→R: apply accumulated right trims to each slide, then accumulate its own
		 *   3. R→L: apply accumulated left trims to each slide, then accumulate its own
		 *   4. Apply final transforms to DOM
		 *
		 * Left slides:  hinge at right edge (transformOrigin: 'right center')
		 *               → left face comes forward (towards viewer)
		 *               → _leftTrim adjusts spacing on LEFT side
		 *
		 * Right slides: hinge at left edge (transformOrigin: 'left center')
		 *               → right face comes forward (towards viewer)
		 *               → _rightTrim adjusts spacing on RIGHT side
		 *
		 * @param {Object} frame - Complete frame object
		 * @param {Object} utils - Frame utilities
		 */
		render(frame, utils) {
			const { slides, widths, animation, options } = frame;

			const perspective = this.perspective;
			const angularStep = this.angularStep;
			const maxAngle = this.maxAngle;
			const scaleStep = this.scaleStep;
			const { slide: slideWidth, slideAndGap, viewport } = widths;
			const viewportCenter = viewport / 2;
			const trackPos = animation.trackPosition;

			this.renderSlideWidth(slideWidth);
			this.renderTrackPosition(animation);

			// ============================================
			// PASS 1: Angle, trim, and per-slide data
			// ============================================
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];

				// Reset trims
				slide._leftTrim = 0;
				slide._rightTrim = 0;

				// Slide's visual center in viewport coordinates
				const centerX = slide._centerPoint + trackPos;
				const dist = centerX - viewportCenter;
				const normalizedDist = slideAndGap > 0 ? dist / slideAndGap : 0;
				const angle = Math.min(maxAngle, Math.abs(normalizedDist) * angularStep);
				const scaleDist = Math.max(0, Math.abs(normalizedDist) - 1);
				const scale = 1 + Math.pow(scaleDist, 1.5) * scaleStep;
				const effectiveWidth = slideWidth * scale;

				slide._scale = scale;

				if (dist < 0) {
					// Left side: hinge at right edge, left face comes forward
					// Trim adjusts LEFT side spacing
					slide._rotateTransform = `rotateY(${angle}deg)`;
					slide.style.transformOrigin = 'right center';
					if (angle > 0) {
						slide._leftTrim = slideWidth - this.getProjectedWidth(effectiveWidth, angle, perspective);
					}
				} else if (dist > 0) {
					// Right side: hinge at left edge, right face comes forward
					// Trim adjusts RIGHT side spacing
					slide._rotateTransform = `rotateY(${-angle}deg)`;
					slide.style.transformOrigin = 'left center';
					if (angle > 0) {
						slide._rightTrim = slideWidth - this.getProjectedWidth(effectiveWidth, angle, perspective);
					}
				} else {
					// Center slide: flat, no rotation
					slide._rotateTransform = '';
					slide.style.transformOrigin = 'center center';
					slide._scale = 1;
				}

				// Start renderPosition at track-relative position
				slide._renderPosition = slide._trackPosition;

				// Hide slides far outside the visible + buffer range
				// Threshold is slightly larger to account for scaled slides needing more buffer
				if (Math.abs(normalizedDist) > options.slidesPerView / 2 + 3) {
					slide.hide();
				} else {
					slide.show();
				}
			}

			// ============================================
			// PASS 2: L→R — apply accumulated right trims, then accumulate current
			// Closes gaps on the RIGHT side of right-side slides by pulling subsequent
			// slides leftward
			// ============================================
			let rightAccum = 0;
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide._renderPosition -= rightAccum;
				rightAccum += slide._rightTrim;
			}

			// ============================================
			// PASS 3: R→L — apply accumulated left trims, then accumulate current
			// Closes gaps on the LEFT side of left-side slides by pushing preceding
			// slides rightward
			// ============================================
			let leftAccum = 0;
			for (let i = slides.length - 1; i >= 0; i--) {
				const slide = slides[i];
				slide._renderPosition += leftAccum;
				leftAccum += slide._leftTrim;
			}

			// ============================================
			// PASS 4: Apply transforms
			// ============================================
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform =
					`translateX(${slide._renderPosition}px) ` +
					`perspective(${perspective}px) ` +
					(slide._rotateTransform || '') +
					` scale(${slide._scale ?? 1})`;
			}
		}

		destroy() {
			super.destroy();
			this.ctx.track.style.transform = '';
			const slides = this.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.transformOrigin = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'panorama',
	factory: createPanoramaEffect,
});

export default createPanoramaEffect;
